import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { filter, distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { ConfigService } from '../config/config.service';

@Injectable({
  providedIn: 'root',
})
export class GooglePlacesLoaderService {
  private readonly loadStateSubject = new BehaviorSubject<boolean | null>(null);

  readonly isGooglePlacesApiLoaded$: Observable<boolean> = this.loadStateSubject.asObservable().pipe(
    filter((state): state is boolean => state !== null),
    distinctUntilChanged(),
    shareReplay(1),
  );

  init(): void {
    const currentState = this.loadStateSubject.value;

    if (currentState === true) {
      return;
    }

    from(this.loadPlacesScriptAsync()).subscribe({
      next: () => this.loadStateSubject.next(true),
      error: (error) => {
        this.loadStateSubject.next(false);
        console.error('Failed to load Google Places API', error);
      }
    });
  }

  private loadPlacesScriptAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
      const googleMapsKey = ConfigService.environmentGlobal?.google?.maps?.key;

      if (!googleMapsKey) {
        console.warn('Google Places API key not found in configuration. Autocomplete will not work.');
        reject(new Error('Google Places API key not configured'));
        return;
      }

      if (typeof google !== 'undefined' && google?.maps) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;

      script.onload = (): void => {
        this.waitForPlacesApiReady().then(resolve).catch(reject);
      };

      script.onerror = (): void => {
        reject(new Error('Failed to load Google Places API script'));
      };

      document.head.appendChild(script);
    });
  }

  private waitForPlacesApiReady(): Promise<void> {
    return new Promise((resolve, reject) => {
      const maxWaitTime = 10000;
      const pollInterval = 50;
      const startTime = Date.now();

      const checkPlacesApi = (): void => {
        if (window.google?.maps?.importLibrary) {
          resolve();
          return;
        }

        if (Date.now() - startTime > maxWaitTime) {
          const message = 'Timeout waiting for Google Places API';
          console.error(message);
          reject(new Error(message));
          return;
        }

        setTimeout(checkPlacesApi, pollInterval);
      };

      checkPlacesApi();
    });
  }

  isGooglePlacesApiLoaded(): boolean {
    return this.loadStateSubject.value === true && typeof google !== 'undefined' && !!google?.maps?.places;
  }
}
